import { useRequiredContext } from "@redotech/react-util/context";
import CodeIcon from "@redotech/redo-icons/code-02_filled.svg";
import FunctionIcon from "@redotech/redo-icons/lightning-01.svg";
import FunctionFilledIcon from "@redotech/redo-icons/lightning-01_filled.svg";
import LogsIcon from "@redotech/redo-icons/list.svg";
import LogsFilledIcon from "@redotech/redo-icons/list_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";

// Developer portal is restricted to these specific accounts while it's in early
// development; widen this (or swap for a permission/flag) before a broader rollout.
const ALLOWED_DEVELOPER_EMAILS = new Set([
  "bcarper@getredo.com",
  "dev@getredo.com",
]);

export function useDeveloperNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const user = useContext(UserContext);

  if (!user?.email || !ALLOWED_DEVELOPER_EMAILS.has(user.email.toLowerCase())) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "developer",
    hasNotification: false,
    UnfilledIcon: CodeIcon,
    FilledIcon: CodeIcon,
    type: "link",
    href: routes.developerFunctionsHref,
    pathRoot: routes.developerBaseUrl,
    friendlyName: "Developer",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "developer",
    name: "Developer",
    NavContent: SecondaryDeveloperNavigation,
  };

  return { primaryTab, secondaryNavigationData, isActiveProduct: true };
}

const SecondaryDeveloperNavigation = memo(
  function SecondaryDeveloperNavigation() {
    const routes = useRequiredContext(MerchantRoutesContext);
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: "Developer",
    });

    return (
      <TreeContext.Provider value={nodeId}>
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.developerFunctionsHref }}
          FilledIcon={FunctionFilledIcon}
          friendlyName="Functions"
          id="developer-functions"
          UnfilledIcon={FunctionIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.developerLogsHref }}
          FilledIcon={LogsFilledIcon}
          friendlyName="Logs"
          id="developer-logs"
          UnfilledIcon={LogsIcon}
        />
      </TreeContext.Provider>
    );
  },
);
